import type { MailboxInfo } from "owa-client-types";
import { type ImplementedCopilotFeatures, type CopilotSettingsStore, shouldDisableCopilot, } from "owa-copilot-settings-store";
import { getApplicationSettings } from "owa-application-settings";
import checkLanguage from "./checkLanguage";
import createStoreCheckForGenericEnablement from "./createStoreCheckForGenericEnablement";
import { isGlobalFeatureEnabled, isAccountFeatureEnabled } from "owa-feature-flags";
import { getCoprincipalMailboxInfo, getGlobalSettingsAccountMailboxInfo, } from "owa-account-source-list-store";
import { getUnifiedInboxResolvedMailboxInfoOrSelf } from "owa-session-store";
import { getIsCopilotUIDisabled } from "owa-mail-copilot-settings/lib/selectors/getIsCopilotUIDisabled";
import type { CopilotEnablementDiagnostics } from "owa-copilot-settings-store/lib/utils/enablementCodes/CopilotEnablementTypes";
import { CopilotFeatureEnablementCodes } from "owa-copilot-settings-store/lib/utils/enablementCodes/CopilotFeatureEnablementCodes";

const isCopilotFeatureEnabled: (scenario: ImplementedCopilotFeatures, mailboxInfo: MailboxInfo, skipLanguageCheck?: boolean, shouldIgnoreUserPreferences?: boolean, diagnostics?: CopilotEnablementDiagnostics) => boolean | undefined = (scenario, mailboxInfo, skipLanguageCheck?, shouldIgnoreUserPreferences?, diagnostics?) => {
    // MyOutlook (Personal OS) mock mode: fully simulate the experience for
    // accounts that don't actually carry the PersonalOS scenario in their
    // Copilot settings response. We fall back to the mock ahead of the store /
    // fetch-state checks — the wrapper below can return `undefined` before ever
    // consulting the settings when Copilot isn't enabled/initialized — so the
    // folder-pane section and full page render off the mock service adapter
    // regardless of Copilot settings state. However, if the user genuinely has
    // PersonalOS enabled, we ignore the mock flag entirely and defer to the
    // real enablement so the mock never replaces a working experience.
    if (scenario === "PersonalOS") {
        const genuineEnablement = genericCopilotFeatureCheck(scenario, mailboxInfo, skipLanguageCheck, shouldIgnoreUserPreferences, diagnostics);
        if (genuineEnablement) {
            return genuineEnablement;
        }
        if (isAccountFeatureEnabled("acct-personalos-mock", mailboxInfo, true /* dontThrowIfNotInit */)) {
            return true;
        }
        return genuineEnablement;
    }
    return genericCopilotFeatureCheck(scenario, mailboxInfo, skipLanguageCheck, shouldIgnoreUserPreferences, diagnostics);
};

/**
 * True when the MyOutlook (Personal OS) feature is genuinely enabled for the
 * account — i.e. the real Copilot enablement path returns true, ignoring the
 * `acct-personalos-mock` demo flag. Mock-mode callers use this to suppress the
 * mock when the user actually has the feature so the mock never shadows a real,
 * working experience.
 */
export function isPersonalOSGenuinelyEnabled(mailboxInfo: MailboxInfo): boolean {
    return !!genericCopilotFeatureCheck("PersonalOS", mailboxInfo);
}

const genericCopilotFeatureCheck: (scenario: ImplementedCopilotFeatures, mailboxInfo: MailboxInfo, skipLanguageCheck?: boolean, shouldIgnoreUserPreferences?: boolean, diagnostics?: CopilotEnablementDiagnostics) => boolean | undefined = 
// eslint-disable-next-line owa-custom-rules/no-side-effects -- BASELINE, DO NOT COPY-PASTE
createStoreCheckForGenericEnablement<ImplementedCopilotFeatures, boolean>("CopilotEnabled", isCopilotFeatureEnabledInternal);

export default isCopilotFeatureEnabled;

function isCopilotFeatureEnabledInternal(store: CopilotSettingsStore, language: string, scenario: ImplementedCopilotFeatures, mailboxInfo: MailboxInfo, skipLanguageCheck?: boolean, shouldIgnoreUserPreferences?: boolean, diagnostics?: CopilotEnablementDiagnostics) {
    // We also need to have a check here as boot cache will bypass fetchCopilotSettings and directly dump settings into store.
    const mailboxInfoForCopilotCheck = getUnifiedInboxResolvedMailboxInfoOrSelf(mailboxInfo);
    if (shouldDisableCopilot(mailboxInfoForCopilotCheck, diagnostics)) {
        return false;
    }
    
    if (!shouldIgnoreUserPreferences && getIsCopilotUIDisabled(mailboxInfo)) {
        diagnostics &&
            (diagnostics.code = CopilotFeatureEnablementCodes.COPILOT_DISABLED_USER_SETTING);
        return false;
    }
    
    if (!store.isEnabled) {
        diagnostics && (diagnostics.code = CopilotFeatureEnablementCodes.COPILOT_STORE_NOT_ENABLED);
        return false;
    }
    
    const featureEnabled = store.featureInfo.get(scenario);
    if (featureEnabled?.isEnabled) {
        const coprincipalMailboxInfo = mailboxInfo.type === "GroupMailbox"
            ? (getCoprincipalMailboxInfo(mailboxInfo) ?? getGlobalSettingsAccountMailboxInfo())
            : mailboxInfo;
        const enabledInECS = isEnabledInECS(scenario, coprincipalMailboxInfo);
        const languageSupported = skipLanguageCheck ||
            language === "qps-ploc" ||
            checkLanguage(language, featureEnabled.langauges);
        
        if (diagnostics) {
            if (!enabledInECS) {
                diagnostics.code = CopilotFeatureEnablementCodes.COPILOT_DISABLED_ECS;
            }
            else if (!languageSupported) {
                diagnostics.code = CopilotFeatureEnablementCodes.COPILOT_UNSUPPORTED_LANGUAGE;
            }
        }
        
        return enabledInECS && languageSupported;
    }
    diagnostics &&
        (diagnostics.code = CopilotFeatureEnablementCodes.COPILOT_STORE_FEATURE_NOT_ENABLED);
    return false;
}

function isEnabledInECS(scenario: ImplementedCopilotFeatures, mailboxInfo: MailboxInfo): boolean {
    // Don't error if settings aren't initialized yet (e.g. during account addition);
    // default settings disable all features, which is the correct fallback behavior.
    const copilotSettings = getApplicationSettings("Copilot", mailboxInfo, true /* dontErrorIfNotInitialized */);
    switch (scenario) {
        case "AttachmentSummarization":
            return copilotSettings.attachmentSummarizationEnabled;
        case "Elaborate":
            return copilotSettings.elaborateEnabled;
        case "Summarize":
            return copilotSettings.summarizeEnabled;
        case "Coach":
        case "CoachApply":
            return copilotSettings.coachEnabled;
        case "SuggestedDrafts":
            return copilotSettings.suggestedDraftsEnabled;
        case "SuggestCategory":
            return isGlobalFeatureEnabled("time-copilot-categories-row-v2");
        case "ExplainPlacesReport":
            return true; // client side feature flag.
        case "Rewrite":
            return isGlobalFeatureEnabled("mon-copilot-elaborate-rewrite");
        case "AgendaGeneration":
            return isGlobalFeatureEnabled("time-copilot-draft-intelligent-agenda");
        case "MeetingPrepSummary":
            // Used by FetchCopilotSettings - Service: https://ecs.skype.com/OlkCopilot/OlkCopilot/configurations/1116121
            // getApplicationSettings('Copilot', mailboxInfo).meetingPrepSummaryEnabled - Client: https://ecs.skype.com/Substrate/OutlookWebClient/configurations/1092852
            return copilotSettings.meetingPrepSummaryEnabled;
        case "ReplyByMeeting":
            // Used by FetchCopilotSettings - Service: https://ecs.skype.com/OlkCopilot/OlkCopilot/configurations/1123944
            // getApplicationSettings('Copilot', mailboxInfo).meetingPrepSummaryEnabled - Client: https://ecs.skype.com/Substrate/OutlookWebClient/configurations/1092852
            return copilotSettings.replyByMeetingEnabled;
        case "Chat":
            // Used by FetchCopilotSettings - Service: https://ecs.skype.com/OlkCopilot/OlkCopilot/configurations/1098830
            return true; // Rely on license check for Chat.
        case "Inbox":
            // https://ecs.skype.com/Substrate/OutlookWebClient/configurations/1092852
            return copilotSettings.prioritizeEnabled;
        case "TimeCopilotChat":
            return copilotSettings.timeCopilotChatEnabled;
        case "VisualTheming":
            return isGlobalFeatureEnabled("fwk-copilot-themes");
        case "MChat":
            return true; // Rely on license check for MChat.
        case "AutoResourceBooking":
            // https://ecs.skype.com/Substrate/OutlookWebClient/configurations/1092852
            return copilotSettings.autoResourceBookingEnabled;
        case "BizChatHandoff":
            return true;
        case "AttendeeColocation":
            // Service: https://ecs.skype.com/OlkCopilot/OlkCopilot/configurations/1569405
            // Client: https://ecs.skype.com/Substrate/OutlookWebClient/configurations/1092852
            return copilotSettings.attendeeColocationEnabled;
        case "RSVPSuggestions":
            return isGlobalFeatureEnabled("time-copilot-rsvp-suggestions");
        case "ManagedEvents":
            // Managed Events is rolled out to 100% WW on OCS + frontier rings.
            // Overall rollout is gated by client flight `time-copilot-managed-events`.
            return true;
        case "PriorityCalendar":
            return true; // Rely on license check and dev flight for Priority Calendar.
        case "CalendarInstructions":
            // Dev flight to disable CalendarInstructions for testing
            return !isGlobalFeatureEnabled("cal-ai-dev-disable-calendar-instructions");
        case "InboxPrioritizationOnDemand":
            return isGlobalFeatureEnabled("tri-prioritize-on-demand");
        case "CalendarManageEventInstructions":
            return true;
        case "CalendarAutoRoomBookingInstructions":
            return true;
        case "MailCoauthoring":
            return true;
        case "DailyBriefing":
            return isGlobalFeatureEnabled("mon-copilot-daily-briefings");
        case "ProactiveDrafts":
            return isGlobalFeatureEnabled("mon-copilot-proactive-drafts-view");
        case "SuggestedUserActions":
            return true;
        case "PersonalOS":
            return isAccountFeatureEnabled("acct-personalos-enabled", mailboxInfo);
        case "EmailPersonalization":
            return isAccountFeatureEnabled("mon-copilot-email-personalization", mailboxInfo);
        default:
            return false;
    }
}
